import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { useDispatch, useSelector } from 'react-redux'

import { enhancedApi as webApi } from '../../common/redux/api/enhancedWebApi'
import companyReducer from '../../common/redux/features/company/companySlice'
import entitlementsReducer from '../../common/redux/features/entitlements/entitlementsSlice'
import portalSessionResetReducer from '../../common/redux/features/portal-session-reset/portalSessionResetSlice'
import userReducer, { reset } from '../../common/redux/features/user/userSlice'

const createRootReducers = () => {
    const rootReducerFn = combineReducers({
        company: companyReducer,
        entitlements: entitlementsReducer,
        portalSessionReset: portalSessionResetReducer,
        user: userReducer,
        [webApi.reducerPath]: webApi.reducer,
    })

    return (state: Parameters<typeof rootReducerFn>[0], action: Parameters<typeof rootReducerFn>[1]) => {
        return rootReducerFn(reset && action.type === reset.type ? undefined : state, action)
    }
}

export const store = configureStore({
    reducer: createRootReducers(),
    devTools: process.env.NODE_ENV === 'development',
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat([webApi.middleware]),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const preloadedStoreFactory = (preloadedState: Partial<RootState>) => {
    return configureStore({
        reducer: createRootReducers(),
        preloadedState: { ...store.getState(), ...preloadedState },
        devTools: process.env.NODE_ENV === 'development',
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat([webApi.middleware]),
    })
}

setupListeners(store.dispatch)

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
